/**
 * @fileoverview Página responsável pela autenticação de entrada na plataforma.
 */

import React, { useState, useContext, useCallback } from "react";

import Button from "@material-ui/core/Button";
import CssBaseline from "@material-ui/core/CssBaseline";
import TextField from "@material-ui/core/TextField";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import Container from "@material-ui/core/Container";
import InputAdornment from "@material-ui/core/InputAdornment";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import { versionSystem } from "../../../package.json";
import { i18n } from "../../translate/i18n";
import { nomeEmpresa } from "../../../package.json";
import { AuthContext } from "../../context/Auth/AuthContext";
import DynamicLogo from "../../components/DynamicLogo";
import { IconButton, Popover } from "@material-ui/core";
import LanguageControl from "../../components/LanguageControl";


import { Eye, EyeOff, Globe, ShieldCheck, KeyRound, ArrowLeft, Smartphone } from "lucide-react";
import MfaSetupDialog from "../../components/MfaSetupDialog";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const Copyright = ({ className }) => {
	return (
		<Typography variant="body2" align="center" className={className}>
			{`Copyright © ${nomeEmpresa} ${versionSystem} - `}
			{new Date().getFullYear()}
		</Typography>
	);
};

const useStyles = makeStyles(theme => ({
	root: {
		width: "100vw",
		height: "100vh",
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, #000000 0%, #0a0e27 50%, #16213e 100%)'
			: '#ffffff',
		backgroundRepeat: "no-repeat",
		backgroundSize: "100% 100%",
		backgroundPosition: "center",
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		textAlign: "center",
		position: "relative",
		overflow: "hidden",
		'&::before': theme.palette.type === 'dark' ? {
			content: '""',
			position: 'absolute',
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			background: 'radial-gradient(circle at 20% 50%, rgba(91, 124, 255, 0.15) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(120, 40, 200, 0.15) 0%, transparent 50%)',
			pointerEvents: 'none',
		} : {}
	},
	paper: {
		background: theme.palette.type === 'dark'
			? '#1A1A1A'
			: '#ffffff',
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		padding: "55px 40px",
		borderRadius: "24px",
		border: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.2)'
			: '1px solid #e0e0e0',
		boxShadow: theme.palette.type === 'dark'
			? '0 20px 60px 0 rgba(0, 0, 0, 0.5), 0 0 80px 0 rgba(91, 124, 255, 0.1)'
			: '0 4px 20px rgba(0, 0, 0, 0.08)',
		position: 'relative',
		zIndex: 1,
		transition: 'all 0.3s ease',
		'&:hover': {
			transform: 'translateY(-2px)',
			boxShadow: theme.palette.type === 'dark'
				? '0 25px 70px 0 rgba(0, 0, 0, 0.6), 0 0 100px 0 rgba(91, 124, 255, 0.15)'
				: '0 8px 30px rgba(0, 0, 0, 0.12)',
		}
	},
	avatar: {
		margin: theme.spacing(1),
		backgroundColor: theme.palette.secondary.main,
	},
	form: {
		width: "100%",
		marginTop: theme.spacing(1),
		display: 'flex',
		flexDirection: 'column',
	},
	logoFrame: {
		width: 240,
		height: 136,
		maxWidth: '80%',
		marginBottom: 20,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		overflow: 'hidden',
	},
	logoScale: {
		flexShrink: 0,
		transform: 'scale(0.5)',
		transformOrigin: 'center',
	},
	submit: {
		margin: theme.spacing(3, 0, 2),
		background: theme.palette.primary.main,
		color: theme.palette.primary.contrastText,
		fontWeight: 500,
		fontSize: '1rem',
		padding: '12px',
		borderRadius: '12px',
		textTransform: 'none',
		boxShadow: "none",
		transition: 'all 0.3s ease',
		'&:hover': {
			background: theme.palette.buttonHover || theme.palette.primary.dark,
			boxShadow: "none",
			transform: 'translateY(-1px)',
		},
		'&.Mui-disabled': {
			background: theme.palette.primary.main,
			color: theme.palette.primary.contrastText,
			opacity: 0.7,
			transform: 'none',
		},
	},
	submitSpinner: {
		color: theme.palette.primary.contrastText,
		marginRight: 8,
	},
	capsLockBadge: {
		display: 'inline-flex',
		alignItems: 'center',
		gap: 6,
		marginTop: 6,
		padding: '4px 10px',
		borderRadius: 999,
		fontSize: '0.72rem',
		fontWeight: 600,
		letterSpacing: '0.04em',
		textTransform: 'uppercase',
		color: theme.palette.type === 'dark' ? '#fbbf24' : '#b45309',
		background: theme.palette.type === 'dark'
			? 'rgba(251, 191, 36, 0.12)'
			: 'rgba(251, 191, 36, 0.18)',
		border: theme.palette.type === 'dark'
			? '1px solid rgba(251, 191, 36, 0.3)'
			: '1px solid rgba(180, 83, 9, 0.25)',
		alignSelf: 'flex-start',
		animation: '$capsLockPulse 1.6s ease-in-out infinite',
		'& svg': {
			width: 12,
			height: 12,
		},
	},
	'@keyframes capsLockPulse': {
		'0%, 100%': { opacity: 0.92 },
		'50%': { opacity: 1 },
	},
	powered: {
		color: "white"
	},
	languageControl: {
		position: "absolute",
		top: 20,
		left: 20,
		zIndex: 10,
	},
	languageButton: {
		background: theme.palette.type === 'dark'
			? 'rgba(255, 255, 255, 0.08)'
			: 'rgba(255, 255, 255, 0.9)',
		borderRadius: '14px',
		border: theme.palette.type === 'dark'
			? '1px solid rgba(255, 255, 255, 0.1)'
			: '1px solid rgba(0, 0, 0, 0.08)',
		padding: '10px',
		transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
		boxShadow: theme.palette.type === 'dark'
			? '0 4px 20px rgba(0, 0, 0, 0.3)'
			: '0 4px 20px rgba(0, 0, 0, 0.08)',
		'&:hover': {
			background: theme.palette.type === 'dark'
				? 'rgba(91, 124, 255, 0.15)'
				: 'rgba(91, 124, 255, 0.08)',
			borderColor: theme.palette.type === 'dark'
				? 'rgba(91, 124, 255, 0.3)'
				: 'rgba(91, 124, 255, 0.25)',
			transform: 'scale(1.05)',
			boxShadow: theme.palette.type === 'dark'
				? '0 6px 25px rgba(91, 124, 255, 0.2)'
				: '0 6px 25px rgba(91, 124, 255, 0.15)',
		},
		'& svg': {
			color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.9)' : '#5b7cff',
			fontSize: '22px',
		}
	},
	languagePopover: {
		'& .MuiPopover-paper': {
			background: theme.palette.type === 'dark'
				? 'rgba(26, 26, 26, 0.95)'
				: 'rgba(255, 255, 255, 0.98)',
			borderRadius: '16px',
			border: theme.palette.type === 'dark'
				? '1px solid rgba(255, 255, 255, 0.1)'
				: '1px solid rgba(0, 0, 0, 0.06)',
			boxShadow: theme.palette.type === 'dark'
				? '0 20px 50px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(91, 124, 255, 0.1)'
				: '0 20px 50px rgba(0, 0, 0, 0.12)',
			marginTop: '8px',
			overflow: 'hidden',
		}
	},
	textField: {
		'& .MuiOutlinedInput-root': {
			borderRadius: '12px',
			backgroundColor: theme.palette.type === 'dark'
				? 'rgba(255, 255, 255, 0.05)'
				: 'rgba(0, 0, 0, 0.02)',
			transition: 'all 0.3s ease',
			'& fieldset': {
				borderColor: theme.palette.type === 'dark'
					? 'rgba(255, 255, 255, 0.1)'
					: 'rgba(0, 0, 0, 0.1)',
				transition: 'all 0.3s ease',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.type === 'dark'
					? 'rgba(91, 124, 255, 0.5)'
					: 'rgba(102, 126, 234, 0.5)',
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.type === 'dark'
					? '#5b7cff'
					: '#667eea',
				borderWidth: '2px',
			},
			'&.Mui-focused': {
				backgroundColor: theme.palette.type === 'dark'
					? 'rgba(91, 124, 255, 0.08)'
					: 'rgba(102, 126, 234, 0.08)',
			}
		},
		'& .MuiInputLabel-outlined': {
			color: theme.palette.type === 'dark'
				? 'rgba(255, 255, 255, 0.7)'
				: 'rgba(0, 0, 0, 0.6)',
			'&.Mui-focused': {
				color: theme.palette.type === 'dark'
					? '#5b7cff'
					: '#667eea',
			}
		}
	},
	mfaHeader: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: 6,
		marginBottom: 12,
	},
	mfaShieldBadge: {
		width: 44,
		height: 44,
		borderRadius: 14,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		background: theme.palette.type === 'dark'
			? 'linear-gradient(135deg, rgba(91, 124, 255, 0.18) 0%, rgba(157, 124, 255, 0.18) 100%)'
			: 'linear-gradient(135deg, rgba(61, 92, 253, 0.10) 0%, rgba(124, 58, 237, 0.10) 100%)',
		border: theme.palette.type === 'dark'
			? '1px solid rgba(91, 124, 255, 0.25)'
			: '1px solid rgba(61, 92, 253, 0.15)',
		color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
	},
	mfaTitle: {
		fontSize: '1rem',
		fontWeight: 600,
		color: theme.palette.type === 'dark' ? '#f1f5f9' : '#1a1a2e',
		marginTop: 4,
	},
	mfaSubtitle: {
		fontSize: '0.78rem',
		color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.55)' : '#64748b',
		textAlign: 'center',
		lineHeight: 1.4,
	},
	otpField: {
		marginTop: 18,
		'& .MuiOutlinedInput-root': {
			borderRadius: '14px',
			backgroundColor: theme.palette.type === 'dark'
				? 'rgba(255, 255, 255, 0.04)'
				: 'rgba(0, 0, 0, 0.02)',
			transition: 'all 0.25s ease',
			'& fieldset': {
				borderColor: theme.palette.type === 'dark'
					? 'rgba(255, 255, 255, 0.08)'
					: 'rgba(0, 0, 0, 0.08)',
			},
			'&:hover fieldset': {
				borderColor: theme.palette.type === 'dark'
					? 'rgba(91, 124, 255, 0.45)'
					: 'rgba(102, 126, 234, 0.45)',
			},
			'&.Mui-focused fieldset': {
				borderColor: theme.palette.type === 'dark' ? '#5b7cff' : '#667eea',
				borderWidth: '2px',
			},
			'&.Mui-focused': {
				backgroundColor: theme.palette.type === 'dark'
					? 'rgba(91, 124, 255, 0.06)'
					: 'rgba(102, 126, 234, 0.06)',
				boxShadow: theme.palette.type === 'dark'
					? '0 0 0 4px rgba(91, 124, 255, 0.12)'
					: '0 0 0 4px rgba(102, 126, 234, 0.10)',
			},
		},
		'& input': {
			fontFamily: '"SF Mono", "JetBrains Mono", Menlo, Consolas, monospace',
			fontWeight: 600,
			padding: '18px 16px',
		},
	},
	mfaActionsRow: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
		marginTop: 8,
		gap: 4,
	},
	mfaLinkBtn: {
		fontSize: '0.7rem',
		fontWeight: 500,
		textTransform: 'none',
		padding: '4px 6px',
		minWidth: 0,
		minHeight: 28,
		whiteSpace: 'nowrap',
		color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.55)' : '#64748b',
		'& .MuiButton-startIcon': {
			marginRight: 4,
			marginLeft: 0,
		},
		'& svg': {
			opacity: 0.75,
		},
		'&:hover': {
			background: 'transparent',
			color: theme.palette.type === 'dark' ? '#7c9aff' : '#5b7cff',
			'& svg': {
				opacity: 1,
			},
		},
	},
	copyright: {
		color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.9)' : 'rgba(0, 0, 0, 0.6)',
		fontWeight: 500,
		textShadow: theme.palette.type === 'dark' ? '0 2px 10px rgba(0, 0, 0, 0.3)' : 'none',
		'& a': {
			color: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.95)' : '#5b7cff',
			textDecoration: 'none',
			fontWeight: 600,
			transition: 'all 0.2s ease',
			'&:hover': {
				color: theme.palette.type === 'dark' ? '#ffffff' : '#4a6ae8',
				textShadow: theme.palette.type === 'dark' ? '0 0 10px rgba(255, 255, 255, 0.5)' : 'none',
			}
		}
	}
}));

const Login = () => {
	const classes = useStyles();

	const [user, setUser] = useState({ email: "", password: "" });
	const [showPassword, setShowPassword] = useState(false);
	const [loading, setLoading] = useState(false);
	const [errors, setErrors] = useState({ email: "", password: "" });
	const [capsLockOn, setCapsLockOn] = useState(false);

	const [step, setStep] = useState("credentials");
	const [mfaTicket, setMfaTicket] = useState("");
	const [setupTicket, setSetupTicket] = useState("");
	const [otp, setOtp] = useState("");
	const [useBackup, setUseBackup] = useState(false);
	const [backupCode, setBackupCode] = useState("");

	const [anchorElLanguage, setAnchorElLanguage] = useState(null);

	const { handleLogin, handleLoginMfa } = useContext(AuthContext);

	const handleChangeInput = e => {
		setUser({ ...user, [e.target.name]: e.target.value });
		if (errors[e.target.name]) {
			setErrors({ ...errors, [e.target.name]: "" });
		}
	};

	const validate = useCallback(() => {
		const next = { email: "", password: "" };
		if (!user.email) next.email = i18n.t("login.errors.emailRequired");
		else if (!EMAIL_RE.test(user.email)) next.email = i18n.t("login.errors.emailInvalid");
		if (!user.password) next.password = i18n.t("login.errors.passwordRequired");
		setErrors(next);
		return !next.email && !next.password;
	}, [user.email, user.password]);

	const handleSubmit = async e => {
		e.preventDefault();
		if (loading) return;
		if (!validate()) return;
		setLoading(true);
		try {
			const result = await handleLogin(user);
			if (result?.mfaRequired) {
				setMfaTicket(result.mfaTicket);
				setStep("otp");
			} else if (result?.mfaSetupRequired) {
				setSetupTicket(result.setupTicket);
				setStep("setup");
			}
		} finally {
			setLoading(false);
		}
	};

	const handleSubmitOtp = async e => {
		e.preventDefault();
		if (loading) return;
		setLoading(true);
		try {
			await handleLoginMfa({
				mfaTicket,
				otp: useBackup ? undefined : otp,
				backupCode: useBackup ? backupCode : undefined,
			});
		} finally {
			setLoading(false);
		}
	};

	const handleBackToCredentials = () => {
		setStep("credentials");
		setMfaTicket("");
		setOtp("");
		setBackupCode("");
		setUseBackup(false);
	};

	const handleCapsLock = e => {
		if (typeof e.getModifierState === "function") {
			setCapsLockOn(e.getModifierState("CapsLock"));
		}
	};

	const handlemenuLanguage = (event) => {
		setAnchorElLanguage(event.currentTarget);
	};

	const handleCloseMenuLanguage = () => {
		setAnchorElLanguage(null);
	};

	const handleClickShowPassword = () => {
		setShowPassword(prev => !prev);
	};

	const handleMouseDownPassword = (event) => {
		event.preventDefault();
	};
	
	return (
		<div className={classes.root}>
		<div className={classes.languageControl}>
			<IconButton
				className={classes.languageButton}
				aria-label="Select language"
				aria-controls="language-popover"
				aria-haspopup="true"
				onClick={handlemenuLanguage}
			>
				<Globe />
			</IconButton>
			<Popover
				id="language-popover"
				className={classes.languagePopover}
				anchorEl={anchorElLanguage}
				open={Boolean(anchorElLanguage)}
				onClose={handleCloseMenuLanguage}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "left",
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "left",
				}}
			>
				<LanguageControl showHeader={true} />
			</Popover>
		</div>
		<Container component="main" maxWidth="xs">
			<CssBaseline/>
			<div className={classes.paper}>
				<div className={classes.logoFrame}>
					<div className={classes.logoScale}>
						<DynamicLogo alt="Logo" />
					</div>
				</div>
				{step === "otp" && (
					<form className={classes.form} noValidate onSubmit={handleSubmitOtp}>
						<div className={classes.mfaHeader}>
							<div className={classes.mfaShieldBadge}>
								<ShieldCheck size={22} strokeWidth={1.75} />
							</div>
							<span className={classes.mfaTitle}>{i18n.t("mfa.login.title")}</span>
							<span className={classes.mfaSubtitle}>
								{useBackup
									? i18n.t("mfa.login.backupHint") || i18n.t("mfa.login.backupLabel")
									: i18n.t("mfa.login.otpHint") || i18n.t("mfa.login.otpLabel")}
							</span>
						</div>
						{!useBackup && (
							<TextField
								className={classes.otpField}
								variant="outlined"
								fullWidth
								value={otp}
								onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
								autoFocus
								disabled={loading}
								placeholder="••••••"
								inputProps={{
									inputMode: "numeric",
									maxLength: 6,
									"aria-label": i18n.t("mfa.login.otpLabel"),
									style: {
										textAlign: "center",
										fontSize: 26,
										letterSpacing: 12,
									},
								}}
							/>
						)}
						{useBackup && (
							<TextField
								className={classes.otpField}
								variant="outlined"
								fullWidth
								value={backupCode}
								onChange={(e) => setBackupCode(e.target.value.toUpperCase().slice(0, 9))}
								autoFocus
								disabled={loading}
								placeholder="XXXX-XXXX"
								inputProps={{
									"aria-label": i18n.t("mfa.login.backupLabel"),
									style: {
										textAlign: "center",
										fontSize: 20,
										letterSpacing: 6,
									},
								}}
							/>
						)}
						<Button
							type="submit"
							fullWidth
							variant="contained"
							color="primary"
							className={classes.submit}
							disabled={loading || (useBackup ? backupCode.length < 8 : otp.length < 6)}
						>
							{loading && <CircularProgress size={18} className={classes.submitSpinner} />}
							{i18n.t("mfa.login.verify")}
						</Button>
						<div className={classes.mfaActionsRow}>
							<Button
								className={classes.mfaLinkBtn}
								onClick={() => setUseBackup(!useBackup)}
								disabled={loading}
								startIcon={useBackup
									? <Smartphone size={13} strokeWidth={1.75} />
									: <KeyRound size={13} strokeWidth={1.75} />}
							>
								{useBackup ? i18n.t("mfa.login.useOtp") : i18n.t("mfa.login.useBackup")}
							</Button>
							<Button
								className={classes.mfaLinkBtn}
								onClick={handleBackToCredentials}
								disabled={loading}
								startIcon={<ArrowLeft size={13} strokeWidth={1.75} />}
							>
								{i18n.t("mfa.login.backToPassword")}
							</Button>
						</div>
					</form>
				)}

				{step === "credentials" && (
				<form className={classes.form} noValidate onSubmit={handleSubmit}>
					<TextField
						className={classes.textField}
						variant="outlined"
						margin="normal"
						fullWidth
						id="email"
						label={i18n.t("login.form.email")}
						name="email"
						value={user.email}
						onChange={handleChangeInput}
						autoComplete="email"
						autoFocus
						disabled={loading}
						error={Boolean(errors.email)}
						helperText={errors.email}
					/>
					<TextField
						className={classes.textField}
						variant="outlined"
						margin="normal"
						fullWidth
						name="password"
						label={i18n.t("login.form.password")}
						type={showPassword ? "text" : "password"}
						id="password"
						value={user.password}
						onChange={handleChangeInput}
						onKeyDown={handleCapsLock}
						onKeyUp={handleCapsLock}
						autoComplete="current-password"
						disabled={loading}
						error={Boolean(errors.password)}
						helperText={errors.password}
						InputProps={{
							endAdornment: (
								<InputAdornment position="end">
									<IconButton
										aria-label="toggle password visibility"
										onClick={handleClickShowPassword}
										onMouseDown={handleMouseDownPassword}
										edge="end"
										size="small"
										disabled={loading}
									>
										{showPassword ? <Eye size={18} strokeWidth={1.75} /> : <EyeOff size={18} strokeWidth={1.75} />}
									</IconButton>
								</InputAdornment>
							),
						}}
					/>

					<Collapse in={capsLockOn} timeout={220}>
						<span className={classes.capsLockBadge} role="status" aria-live="polite">
							<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round">
								<path d="M12 5l-7 7h4v5h6v-5h4z" />
							</svg>
							{i18n.t("login.capsLockOn")}
						</span>
					</Collapse>

					<Button
						type="submit"
						fullWidth
						variant="contained"
						color="primary"
						className={classes.submit}
						disabled={loading}
						aria-busy={loading}
					>
						{loading && <CircularProgress size={18} className={classes.submitSpinner} />}
						{loading ? i18n.t("login.buttons.submitting") : i18n.t("login.buttons.submit")}
					</Button>
				</form>
				)}

				<MfaSetupDialog
					open={step === "setup"}
					setupTicket={setupTicket}
					forceMode
					onClose={() => {
						setStep("credentials");
						setSetupTicket("");
					}}
				/>

			</div>
			<Box mt={8}><Copyright className={classes.copyright} /></Box>
		</Container>
		</div>
	);
};

export default Login;
